import { useEffect } from 'react';
import { useAppBuilderContext } from 'app/app/editor/context';
import deleteComponentFromPage from 'app/app/editor/page/deleteComponentFromPage';

interface UseDeleteKeyListenerOptions {
    componentUid: string | null;
    onBeforeDelete?: (componentUid: string) => boolean; // Return false to cancel deletion
    onAfterDelete?: (componentUid: string) => void;
    confirmDeletion?: boolean;
    preventDeleteRoot?: boolean;
}

/**
 * Hook that listens for Delete/Backspace keys and deletes the selected component
 * @param options Configuration options
 */
export function useDeleteKeyListener(options: UseDeleteKeyListenerOptions) {
    const { componentUid, onBeforeDelete, onAfterDelete, confirmDeletion = false, preventDeleteRoot = true } = options;

    const ctx = useAppBuilderContext();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check if Delete or Backspace key is pressed
            if (event.key !== 'Delete' && event.key !== 'Backspace') {
                return;
            }

            // Don't delete if typing in an input field
            const target = event.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                return;
            }

            // Prevent default backspace navigation
            if (event.key === 'Backspace') {
                event.preventDefault();
            }

            // Check if a component is selected
            if (!componentUid) {
                return;
            }

            // Prevent deleting the root layout if enabled
            if (preventDeleteRoot && componentUid === ctx.page?.layout?.params?.uid) {
                return;
            }

            // Call onBeforeDelete callback if provided
            if (onBeforeDelete && !onBeforeDelete(componentUid)) {
                return;
            }

            // Delete the component
            const newPage = deleteComponentFromPage(ctx.page, componentUid);
            ctx.setPage(newPage, false);
            ctx.sendMessage({ name: 'reloadPage' });

            // Call onAfterDelete callback if provided
            if (onAfterDelete) {
                onAfterDelete(componentUid);
            }
        };

        // Add event listener
        document.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [componentUid, ctx, onBeforeDelete, onAfterDelete, confirmDeletion, preventDeleteRoot]);
}
